import { useState } from "react";
import type {
  ReactNode,
  SelectHTMLAttributes,
  InputHTMLAttributes,
} from "react";
import {
  ChevronIcon,
  PointIcon,
  TrendDownIcon,
  TrendUpIcon,
} from "../ui/icons";
import { IconHelp as HelpIcon } from "../console/icons";
import { SourceDialog, type StatSource } from "../console/ui";

/**
 * The DashboardShell kit — one page header, one panel, one table, one field.
 *
 * 🔴 WHY THIS MOVED OUT OF `teacher/TeacherUI.tsx`, WHICH IS WHERE IT WAS BORN.
 *
 * The teacher tier built this kit and the tiers above it never got it. So the
 * product shipped three visual languages for one design system: the teacher's
 * screens had cards, tinted table bands and pill navigation, while the school
 * dashboard and the whole platform tier were still `h1 text-dash-lg font-bold`
 * over a bare `<table>` whose every cell repeated `border-b border-line px-3
 * py-2`, with an unstyled native `<select>` sitting on the page. An operator and
 * a head teacher were looking at what read as two different products.
 *
 * A kit that lives inside one feature folder is a kit the next tier copies
 * badly. This is `shared/` for the same reason `DashStatus` and `DataSurface`
 * are: it is the design system, not one tier's taste.
 *
 * ⚠️ THE CONSTRAINT THAT SHAPES ALL OF IT is design-system.md §8: DashboardShell
 * is *"dense and scannable, warm and quiet"*, and its warmth comes from
 * "rounded corners, generous spacing, warm neutrals and a rounded typeface —
 * **never from cartoon elements**". So this is not the kid tier's physical,
 * shadowed, springy material at a smaller size. It is quieter on purpose: an
 * adult reads this between two lessons and must be able to scan it, not admire
 * it.
 *
 * 🔴 AND NO GREY. §1's hardest non-negotiable — *"grey against a warm
 * hand-drawn logo is what made the predecessor build feel corporate"*. Every
 * border here is `line`/`line-strong`, every shadow is `shadow-card`, which is
 * warm-tinted (`rgba(90,60,30,.10)`), and there is not a `slate-` or `gray-` in
 * the file.
 */

/* ────────────────────────────────────────────────────────── constants ── */

/**
 * One field style for every adult surface.
 *
 * ⚠️ `h-11` AND `text-dash-md`, where the platform tier's fields were `h-10` at
 * 15px and the school tier's were `border px-2 py-1` — a spreadsheet cell. An
 * adult types into these standing up, on a classroom laptop, often with a child
 * waiting. The focus ring is the border turning magenta because the browser
 * default is a cool blue-grey, which §1 forbids anywhere.
 */
export const FIELD =
  "h-11 w-full rounded-lg border border-line-strong bg-surface px-3.5 font-dash text-dash-md text-ink outline-none transition-colors duration-150 ease-dash focus:border-primary";

/**
 * The panel every data surface sits in, minus its shadow.
 *
 * ⚠️ THE SHADOW IS SPLIT OFF BECAUSE TWO OF THEM CANNOT BE COMPOSED. Tailwind
 * resolves `shadow-card` against `shadow-raised` by their order in the generated
 * stylesheet, not by their order in a `class` attribute — so appending the
 * heavier one to a string that already carries the lighter one wins or loses by
 * accident. `Panel` picks exactly one.
 */
const PANEL_SHELL = "rounded-lg border border-line bg-surface";

/** The panel every data surface sits in. Radius 16 · warm hairline · warm shadow. */
export const PANEL = `${PANEL_SHELL} shadow-card`;

/* ───────────────────────────────────────────────────────── components ── */

export interface PageHeadProps {
  /** The screen's name. The sidebar highlights it; this states it on the page. */
  title?: ReactNode;
  /** One sentence. What this screen answers, not what it is called. */
  subtitle?: ReactNode;
  /** Buttons that act on the whole screen — export, retry, open. */
  actions?: ReactNode;
}

/**
 * Every screen opens the same way, so a reader never has to re-find the title.
 *
 * ⚠️ THE TITLE IS `dash-xl` (28px) AND NOT BALOO. The marketing page and the
 * child's screens use Baloo for display; this tier does not, because §4 splits
 * the type scale by shell and a rounded display face on a data screen is the
 * "cartoon element" §8 rules out. Weight and size carry the hierarchy instead.
 */
export function PageHead({ title, subtitle, actions }: PageHeadProps) {
  return (
    /*
     * ⚠️ `flex-wrap` WAS ALREADY RIGHT AND IS NOT ENOUGH ON ITS OWN. When the
     * actions wrap onto their own line they land hard against the subtitle,
     * because `gap-4` is the gap BETWEEN the two blocks and the wrap makes it a
     * vertical one. `gap-y` is the same number stated for the axis that only
     * exists on a phone, so the two do not merge into one paragraph.
     */
    <header className="mb-5 flex flex-wrap items-start justify-between gap-x-4 gap-y-3 sm:mb-6">
      {title || subtitle ? (
        <div>
          {title ? (
            /*
             * 🔴 `dash-lg` UNDER `sm`, AND THIS IS THE ONE PLACE THE ROOT
             * FONT-SIZE STRATEGY CANNOT HELP. `responsive.css` deliberately
             * stops scaling below 48rem — narrow is a different design, not a
             * smaller one — so `dash-xl` stays a full 28px on a 360px phone.
             * "Children across the platform" at 28px is three lines of heading
             * before the screen has said anything. 22px is two.
             */
            <h1 className="font-dash text-dash-lg font-extrabold text-ink sm:text-dash-xl">
              {title}
            </h1>
          ) : null}
          {subtitle ? (
            <p className="mt-1.5 max-w-[60ch] font-dash text-dash-sm text-muted">
              {subtitle}
            </p>
          ) : null}
        </div>
      ) : null}
      {actions ? (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </header>
  );
}

/**
 * A sub-heading inside a screen — "Needs attention", "Recent events".
 *
 * 🔴 SENTENCE CASE AT A SMALLER SIZE, NEVER ALL-CAPS. §4: *"No all-caps
 * micro-labels"*, with a single exception for a page-title eyebrow — which this
 * is not.
 */
export function SectionHead({
  children,
  count,
  hint,
  actions,
}: {
  children: ReactNode;
  count?: number;
  /**
   * The sentence under a section title. It exists because the platform tier's
   * sections each carried a paragraph the old markup rendered as a loose `<p>`
   * three elements away from the heading it belonged to.
   */
  hint?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    /*
     * ⚠️ 32px ABOVE, NOT 40. Forty is not on the spacing scale at all
     * (4 · 8 · 12 · 16 · 24 · 32 · 48 · 64) and it was the largest vertical gap
     * in the product — on the platform and school tiers, which are four or five
     * sections deep, it alone pushed the last section below the fold on the
     * 1366×768 laptop §1 designs for. 32 is the next value down and still reads
     * as a break between sections rather than a paragraph gap.
     */
    <div className="mt-7 mb-3 flex flex-wrap items-end justify-between gap-x-3 gap-y-2 sm:mt-8">
      <div>
        <h2 className="flex items-center gap-2 font-dash text-dash-md font-extrabold text-ink">
          {children}
          {count !== undefined && (
            <span className="tabular rounded-pill bg-canvas px-2 py-0.5 text-dash-xs font-bold text-muted">
              {count}
            </span>
          )}
        </h2>
        {hint ? (
          <p className="mt-1 max-w-[70ch] font-dash text-dash-xs text-muted">
            {hint}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

/** A bordered card. Use `flush` when the child is a table that draws its own edges. */
export function Panel({
  children,
  flush = false,
  raised = false,
}: {
  children: ReactNode;
  flush?: boolean;
  /**
   * The heavier shadow, for a panel that is FLOATING rather than sitting in the
   * page — the card inside a `ModalCard`, or a menu.
   *
   * 🔴 IT IS THE DOCUMENTED SPLIT, NOT A PREFERENCE. §5's shadow table assigns
   * `card` to *"dashboard cards"* and `raised` to *"menus, modals"*, and the
   * difference is doing real work: `card` reads as a surface resting on the
   * canvas, which is exactly the wrong claim for something drawn over a blurred
   * overlay with the page still visible behind it.
   *
   * ⚠️ DEFAULT `false`, SO NO EXISTING PANEL MOVES. Every caller that is in the
   * page rather than over it keeps the shadow it has.
   */
  raised?: boolean;
}) {
  /*
   * ⚠️ 16px, NOT 24 — AND IT IS THE SAME INSET `StatCard` NOW USES.
   *
   * A panel and a stat tile are the two rectangles every adult screen is built
   * from, and they sat at 24px and 20px respectively: one off the documented
   * spacing scale (4 · 8 · 12 · 16 · 24 · 32 · 48 · 64), and neither agreeing
   * with the other. On the school and platform tiers, where six or seven panels
   * stack down one page, that difference reads as the surfaces being unrelated
   * — and the whole column as bigger than the data in it.
   *
   * 🔴 THE 24px ITSELF WAS NEVER THE SPEC. §8 asks for *"information-dense and
   * scannable"* and gets its warmth from *"rounded corners, generous spacing,
   * warm neutrals"* — generous is the RADIUS and the gaps BETWEEN cards, both
   * unchanged here. Inside a card, padding a reader has to scan past is not
   * generosity.
   */
  /*
   * ⚠️ 12px ON A PHONE, 16 EVERYWHERE ELSE. The note above argues 16 against 24
   * on a laptop and that argument holds; it does not survive the trip to 360px,
   * where a panel inside the shell's own 16px inset has 32px of the screen's 360
   * spent before the content starts. Four screens of this product nest a panel
   * inside a panel, which doubles it.
   */
  return (
    <div
      className={`${PANEL_SHELL} ${raised ? "shadow-raised" : "shadow-card"} ${flush ? "" : "p-3 sm:p-4"}`}
    >
      {children}
    </div>
  );
}

/**
 * The table wrapper — a card, a warm header band, and horizontal scroll.
 *
 * 🔴 `overflow-x-auto` IS LOAD-BEARING, NOT DEFENSIVE. These tables are the
 * tier's densest surface — the roster carries roll, name, parent, phone and a
 * date, and a long name pushes it past the column budget on its own. Without
 * this the page itself scrolls sideways, which on a trackpad means a reader
 * loses the column headers while reading a row.
 */
export function TableCard({
  children,
  /**
   * What the table is, in two or three words — "Class roster", "Recent events".
   *
   * 🔴 IT IS THE SCROLL REGION'S ACCESSIBLE NAME AND IT IS WHY THE REGION IS
   * REACHABLE AT ALL. A `div` that scrolls is not focusable, so a keyboard
   * reader cannot scroll it sideways — they tab through the cells and the
   * viewport drags along behind, which works until a row has no focusable cell.
   * `tabIndex={0}` fixes that and immediately creates the second problem: an
   * unlabelled focus stop announced as "group". This names it.
   */
  label,
}: {
  children: ReactNode;
  label?: string;
}) {
  return (
    <div className={`${PANEL} overflow-hidden`}>
      {/*
        🔴 THE SIDEWAYS SCROLL IS THE ONLY HONEST ANSWER FOR A DENSE TABLE ON A
        PHONE, AND THE ALTERNATIVE IS WORSE THAN IT LOOKS. The obvious move is
        to reflow each row into a stacked card below `sm`. These tables are
        comparison surfaces — a head teacher scans the *attendance* column down
        twenty children to find the one who is missing. Stacked cards destroy
        the column; the reader has to hold twenty numbers in their head instead
        of running an eye down them. A table that scrolls is still a table.
        `.scroll-x` (responsive.css) carries the touch and overscroll rules.

        ⚠️ `role="region"` ONLY WHEN NAMED. An unnamed region is a landmark with
        no label, which is noise in a screen reader's landmark list — worse than
        not being a landmark at all.
      */}
      <div
        className="scroll-x w-full"
        tabIndex={0}
        role={label ? "region" : undefined}
        aria-label={label}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Table head.
 *
 * ⚠️ A FILLED BAND RATHER THAN A BOTTOM RULE. These tables can be twenty rows
 * of a class list; a hairline under the header disappears the moment the eye is
 * two rows down, and a tinted band survives being scrolled past in peripheral
 * vision.
 */
export function Thead({
  headers,
  sticky = false,
}: {
  headers: readonly ReactNode[];
  /**
   * Pin the header while the rows scroll under it.
   *
   * 🔴 ONLY MEANINGFUL INSIDE A BOUNDED, VERTICALLY-SCROLLING ANCESTOR, which is
   * why it is opt-in rather than always on. A table that grows the page has no
   * scroll container to stick to, so `position: sticky` there would either do
   * nothing or — with `top-0` against the document — detach the header from its
   * own table as the page moved.
   *
   * ⚠️ THE BAND'S OPACITY IS WHAT MAKES IT WORK. `bg-canvas` is already solid, so
   * rows pass underneath rather than showing through — a translucent header over
   * moving rows is unreadable at exactly the moment it is needed.
   */
  sticky?: boolean;
}) {
  return (
    <thead className={sticky ? "sticky top-0 z-10" : undefined}>
      <tr className="bg-canvas text-left">
        {headers.map((h, i) => (
          <th
            key={i}
            /*
             * ⚠️ 12px OF INSET ON A PHONE, 16 FROM `sm`. Five columns each
             * spending 32px on padding is 160px of a 360px screen given to
             * nothing — which is one whole column's worth of the sideways
             * scroll the reader then has to do.
             */
            className="border-b border-line px-3 py-2.5 font-dash text-dash-xs font-bold whitespace-nowrap text-muted sm:px-4 sm:py-3"
          >
            {h}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** A body cell. `num` switches on tabular figures, which every stat needs (§4). */
export function Td({
  children,
  num = false,
  soft = false,
  bold = false,
  className = '',
}: {
  children: ReactNode;
  num?: boolean;
  soft?: boolean;
  bold?: boolean;
  className?: string;
}) {
  return (
    <td
      className={`border-b border-line px-3 py-2.5 font-dash text-dash-sm sm:px-4 sm:py-3 ${num ? "tabular" : ""} ${
        soft ? "text-ink-soft" : "text-ink"
      } ${bold ? "font-bold" : ""} ${className}`}
    >
      {children}
    </td>
  );
}

/**
 * A row that responds to the pointer.
 *
 * ⚠️ HOVER ONLY, NO ZEBRA STRIPING. Alternating fills add a second visual
 * rhythm competing with the skill colours these rows carry, and §12 wants
 * status read from "icon + colour + text" rather than from a row's background.
 */
export function Tr({
  children,
  onClick,
  selected = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  selected?: boolean;
}) {
  return (
    <tr
      onClick={onClick}
      style={selected ? { background: "var(--color-primary-tint)" } : undefined}
      className={`transition-colors duration-150 ease-dash ${selected ? "" : "hover:bg-canvas"} ${
        onClick ? "cursor-pointer" : ""
      }`}
    >
      {children}
    </tr>
  );
}

/* ─────────────────────────────────────────────── the dashboard layer ── */

/**
 * The one all-caps label the type scale permits.
 *
 * ⚠️ §4 forbids all-caps micro-labels *"with a single exception for a page-title
 * eyebrow"* — this is that exception. It answers "where am I" in two words above
 * a heading that answers "what is this".
 */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="mb-1.5 font-dash text-dash-xs font-bold tracking-[0.12em] text-muted uppercase">
      {children}
    </p>
  );
}

/**
 * 🔴 THE SIX TOKENS A STAT CHIP MAY USE, AND WHY IT IS A CLOSED SET.
 *
 * The colour on these cards is **identity, not status** — it makes "children"
 * findable at a glance in the same position every morning. That is exactly why
 * it must not be free: §12 requires status to be *"icon + colour + text, always
 * all three"*, so a card that picked its colour per value would be asserting a
 * status the number does not carry. The palette is fixed per metric, chosen
 * once at each call site, and every one of these is a token — §1: *"no grey,
 * anywhere"*, and `sunshine` is a **fill**, never a text colour (§2), which is
 * why the numeral beside it is `ink`.
 */
export type StatTone =
  "primary" | "sunshine" | "coral" | "leaf" | "indigo" | "teal";

const TONE_FILL: Record<StatTone, string> = {
  primary: "var(--color-primary)",
  sunshine: "var(--color-sunshine)",
  coral: "var(--color-coral)",
  leaf: "var(--color-leaf)",
  indigo: "var(--color-indigo)",
  teal: "var(--color-teal)",
};

/**
 * 🔴 THE THIRD LINE THE SPEC ALWAYS ASKED FOR — *"13 px label → 36 px numeral →
 * 13 px delta"* (`design-system.md` §"Stat tiles") — AND THE TILE HAD TWO.
 *
 * A stat with no comparison is true and unactionable: "83 sittings" cannot be
 * read as good or bad until you know last week was 74, and no teacher holds last
 * week's dashboard in her head. Every tier's stat row had been carrying the
 * label and the numeral and quietly dropping the line that makes them mean
 * something.
 *
 * ⚠️ IT IS A DIRECTION, NOT A VERDICT, WHICH IS WHY `down` IS WARNING AND NEVER
 * DANGER. `StrengthsPanel` states the rule for a child — *"a fact about a term,
 * not an alarm about a family"* — and a quiet week for a class of five-year-olds
 * is a fact about a week: half-term, a bug, a broken tablet trolley. Red would
 * make a teacher answer for it.
 *
 * ⚠️ AND `flat` IS DRAWN, NOT DROPPED. "No change" is something a teacher can
 * act on; an absent line is indistinguishable from a tile that forgot to pass
 * one, which is how a reader learns to stop trusting the arrow that IS there.
 */
export type StatDeltaDirection = "up" | "down" | "flat";

export interface StatDelta {
  direction: StatDeltaDirection;
  /**
   * The change, already written out — *"+9 vs last week"*.
   *
   * 🔴 A STRING RATHER THAN A NUMBER, BECAUSE THE UNIT IS THE CALLER'S AND SO IS
   * THE LANGUAGE. This component cannot know whether 9 means nine sittings, nine
   * children or nine percentage points, and a tile that formats "+9" out of
   * context is one that will eventually print "+9%" beside a count of people.
   */
  label: ReactNode;
}

const DELTA_STYLE: Record<
  StatDeltaDirection,
  { className: string; Icon: (props: { size?: number; strokeWidth?: number }) => ReactNode }
> = {
  /* 🔴 §12 — ICON AND COLOUR AND TEXT, ALL THREE. The arrow alone is invisible
     to a screen reader and to anyone who cannot separate the two hues; the
     colour alone fails §2 outright; the text alone is the thing that survives
     both, so it is the one that carries the number. */
  up: { className: "text-pill-success-fg", Icon: TrendUpIcon },
  down: { className: "text-pill-warning-fg", Icon: TrendDownIcon },
  flat: { className: "text-muted", Icon: PointIcon },
};

/**
 * 🔴 THE ICON SITS IN A TINTED DISC, AND THE NUMERAL NEVER TAKES THE TONE.
 *
 * A coloured numeral would be colour carrying meaning alone the moment two
 * cards sit side by side — and `sunshine` fails contrast as text outright (§2:
 * *"a fill, not a text colour"*). So the tone lives in a 16 %-alpha disc behind
 * a full-strength icon, and the number stays `ink` at every size.
 */
export function StatCard({
  label,
  value,
  caption,
  delta,
  icon,
  tone = "primary",
  size = "md",
  onClick,
  source,
}: {
  label: ReactNode;
  /**
   * 🔴 A NUMBER, A PRE-PAIRED FRACTION, OR A PERCENTAGE WHOSE DENOMINATOR IS IN
   * THE CAPTION — never a bare percentage (OD-39/OD-72).
   *
   * The rule was written as *"never a percentage"* because every percentage the
   * product had at the time was one nobody could put a denominator under —
   * *"Coordination 81%"*, of what? It was never a ban on division. A class
   * accuracy of 72 % over "180 right of 250 answered" satisfies the actual
   * requirement, which is that the reader can always see what the figure is a
   * share OF; a tile that shows the ratio and hides the pair does not.
   */
  value: ReactNode;
  caption?: ReactNode;
  /**
   * Which way this number moved since the comparable window.
   *
   * ⚠️ IT SHARES THE CAPTION'S LINE RATHER THAN TAKING ONE OF ITS OWN. A grid
   * row is as tall as its tallest card, so a third line on one tile pays for
   * itself on every sibling — see the caption's note below. The two read as one
   * sentence anyway: *"+9 vs last week · 21 of 27 children"*.
   */
  delta?: StatDelta;
  icon: ReactNode;
  tone?: StatTone;
  /**
   * How much room the tile takes. `md` is the tile every tier has drawn.
   *
   * ✅ `sm` EXISTS FOR A ROW THAT IS NOT THE POINT OF ITS SCREEN. On the parent
   * overview these four counts are a strip above the child's actual progress —
   * a `dash-2xl` numeral over 16px of padding gave them the visual weight of the
   * report underneath, on a tier specified as *"a 30-second read on a phone"*.
   *
   * ⚠️ IT IS A SIZE, NOT A SECOND DESIGN. Same order (label → numeral →
   * caption), same tone wash, same disc, same radius, same `play-card` lift —
   * §4's tile with its type scale stepped down one notch. A variant that also
   * moved things would be two tiles for a reader to learn.
   *
   * ✅ `xs` IS THE SAME STEP TAKEN ONCE MORE, FOR A ROW OF SIX ON ONE LINE. The
   * teacher overview's six measures at `md` filled 275px above the fold — two
   * banks of large boxes before the chart that answers the question the screen
   * is actually for. At `xs` the same six sit in a single ~105px strip: same
   * order, same wash, same disc, one notch of padding and label off.
   *
   * ⚠️ AND ITS LABEL IS ONE LINE, ALWAYS — the one thing `xs` does that the
   * other two do not. In a 168px column "Sessions this week" wraps and "Play
   * time" does not, so the numerals stop sharing a baseline and a row that is
   * meant to be scanned across becomes six boxes at six heights. An 11px label
   * fits every metric this product has; `truncate` is the guarantee that a
   * longer one shortens ITSELF rather than shoving the figure below it.
   */
  size?: "md" | "sm" | "xs";
  /**
   * 🔴 PASS THIS ONLY WHEN THE TILE REALLY OPENS SOMETHING — it is what turns
   * the card into a `<button>`, and there is deliberately no separate prop for
   * "looks clickable".
   *
   * A tile that lifts under the pointer but goes nowhere is the promise the
   * `play-card` note below refuses to make; a tile that DOES go somewhere but
   * is drawn identically to the three beside it is the same lie told the other
   * way round — the teacher finds the door by accident or not at all. Binding
   * the handler and the affordance to one prop is what makes both impossible.
   */
  onClick?: () => void;
  /** Where the number comes from. Without `onClick`, the tile opens a panel that says so. */
  source?: StatSource;
}) {
  const fill = TONE_FILL[tone];
  const [sourceOpen, setSourceOpen] = useState(false);
  /*
   * ⚠️ NO `min-w`, AND NO `flex-1`. Both were left over from the `flex-wrap`
   * row `StatGrid` replaced. Inside a grid `flex-1` does nothing at all, and
   * `min-w-[10.5rem]` was actively harmful: two columns on a 360 px phone give
   * each card ~156 px, so the minimum forced the row wider than the screen and
   * the parent tier — which is the phone-facing one — scrolled sideways.
   * `min-w-0` lets a long label wrap instead of pushing.
   */
  /*
   * ⚠️ `play-card` — 4px of lift on hover, and nothing else. Every tier's stat
   * row gets it, which is the point: a tile that rises under the pointer reads
   * as a thing rather than as a printed box, and it is the cheapest possible
   * version of that.
   *
   * ⚠️ THE LIFT IS NOT AN OFFER TO CLICK, WHICH IS WHY IT IS THE ONLY THING THE
   * PLAIN CARD GETS. Most of these open nothing, so they take no cursor change
   * and no glow — both would promise a door that is not there. The `onClick`
   * branch below adds exactly what a real door needs and nothing more.
   *
   * `play.css` removes the transform entirely under either reduce-motion
   * switch, without this component asking whether it may move.
   */
  /*
   * ⚠️ NOT `PANEL` ANY MORE, AND THE RADIUS IS UNCHANGED BY THAT. `PANEL` is
   * `rounded-lg border border-line bg-surface shadow-card`, and `--radius-lg` is
   * 1rem — exactly the `radius 16` §"Stat tiles" asks for. What the card can no
   * longer take from the constant is the flat `bg-surface` and the flat
   * `border-line`, because both are now mixed from the tone below. Everything
   * else is spelled out identically so the tile still sits on the same radius
   * and the same shadow as every panel beside it.
   */
  /*
   * ⚠️ ONE STEP DOWN THE SCALE, NOT A SET OF NEW NUMBERS. `sm` keeps the phone's
   * own padding at every width instead of ramping to 16px, and takes the numeral
   * and the disc down one notch each — so the tile reads as the same tile,
   * quieter, rather than as a second design to learn.
   */
  const strip = size === "xs";
  const dense = size === "sm" || strip;
  const shell = `play-card flex min-w-0 flex-col rounded-lg border shadow-card ${
    strip ? "p-2.5" : dense ? "p-3" : "p-3 sm:p-4"
  }`;

  /*
   * 🔴 THE TONE REACHES THE CARD ITSELF, NOT JUST THE 28 px DISC — AND THAT IS
   * THIS COMPONENT'S OWN STATED PURPOSE FINALLY BEING PAID FOR.
   *
   * The note on `StatTone` says the colour here is *"identity, not status — it
   * makes 'children' findable at a glance in the same position every morning"*.
   * A disc 28 px across cannot do that from across a classroom: four white
   * rectangles with four tiny coloured dots read as one grey row, which is what
   * the teacher tier actually looked like. The wash gives each tile a body its
   * colour, so the row is scannable at the distance it is scanned from.
   *
   * ⚠️ IT IS A WASH, NOT A FILL, AND IT SITS IN THE CORNER THE CONTENT LEAVES
   * EMPTY. The gradient is anchored top-RIGHT at 13 % and gone by 60 %, so the
   * colour lands on the dead space beside the label rather than behind the
   * numeral — §4 requires the figure to stay `ink` on `surface`, and a tint
   * running under it would be contrast spent on decoration. `sunshine` is a
   * fill and never a text colour (§2); this is the one thing it may legally do.
   *
   * ⚠️ `color-mix` AGAINST THE TOKENS, NOT HARDCODED PASTELS. Six tones times
   * two mixes is twelve colours nobody has to keep in a palette file, and they
   * follow the tokens if the tokens ever move.
   */
  const surface = {
    background: `radial-gradient(125% 105% at 100% 0%, color-mix(in srgb, ${fill} 13%, transparent) 0%, transparent 60%), var(--color-surface)`,
    borderColor: `color-mix(in srgb, ${fill} 26%, var(--color-line))`,
  };

  const inner = (
    <>
      {/*
        🔴 THE LABEL COMES FIRST AND THE ICON RIDES WITH IT, WHICH IS BOTH THE
        SPEC'S ORDER AND ~50 px OF HEIGHT BACK.
        `design-system.md` §"Stat tiles" is explicit: *"13 px label → 36 px
        numeral → 13 px delta"*. This card used to open with a 40 px disc on a
        row of its own, then the numeral, then the label — so every tile spent a
        whole row on decoration and read bottom-up. Sharing one row with the
        label keeps the colour-as-identity the disc was added for while giving
        the row back.
      */}
      <span className={`flex items-center ${strip ? "gap-1.5" : "gap-2"}`}>
        <span
          aria-hidden="true"
          className={`flex shrink-0 items-center justify-center rounded-full ${
            dense ? "h-6 w-6" : "h-7 w-7"
          }`}
          style={{
            background: `color-mix(in srgb, ${fill} 16%, transparent)`,
            color: fill,
          }}
        >
          {icon}
        </span>
        {/*
          🔴 SENTENCE CASE. §12's Never list names *"all-caps micro-labels
          scattered through the UI"* and §4 allows exactly one exception — *"a
          single eyebrow label above a page title"* — which a stat tile is not.
          `Eyebrow` above is that exception and says so. Four shouting labels in
          a row was the product breaking its own loudest typography rule in the
          most-repeated component it has.
        */}
        <span
          /*
           * ⚠️ THE FULL WORDS STAY REACHABLE WHEN `xs` SHORTENS THEM. Every
           * label in the product fits the strip at the 1366 §1 designs for;
           * below that the longest ("Children who played") loses its last word
           * to the ellipsis, and a title attribute is the cheapest way for that
           * word to still exist. Only when the label IS a string — the tiers
           * pass plain copy, and a node has no text to promise.
           */
          title={strip && typeof label === "string" ? label : undefined}
          className={`min-w-0 font-dash font-bold text-muted ${
            strip ? "truncate text-[11px]" : "text-dash-xs"
          }`}
        >
          {label}
        </span>
        {/*
          🔴 THE CHEVRON IS THE WHOLE VISIBLE DIFFERENCE BETWEEN A TILE THAT
          OPENS AND ONE THAT DOES NOT, so it rides in the label row rather than
          anywhere that would cost the card height. §12 wants an affordance a
          teacher can see without hovering — hover-only cursor changes are
          invisible on the tablets this tier is read on — and one 14 px caret
          against the muted label is the quietest version that still says door.
        */}
        {source && !onClick ? (
          <HelpIcon size={13} aria-hidden="true" className="ml-auto shrink-0 text-muted" />
        ) : null}
        {onClick ? (
          <ChevronIcon
            size={14}
            strokeWidth={3}
            aria-hidden="true"
            className="ml-auto shrink-0 text-muted transition-transform group-hover:translate-x-0.5"
          />
        ) : null}
      </span>

      {/* 🔴 36 px, tabular, `ink` at every tone — §4: *"tabular figures on every
          stat"*, and a coloured numeral would be colour carrying meaning alone. */}
      <span
        className={`tabular leading-none font-extrabold text-ink ${
          dense ? "mt-1.5 text-dash-xl" : "mt-2.5 text-dash-2xl"
        }`}
      >
        {value}
      </span>

      {delta || caption ? (
        /*
         * ⚠️ THE CAPTION IS THE ONE THING THAT CAN STILL STRETCH A ROW, because
         * a grid row is as tall as its tallest card. It is sized for the spec's
         * examples — "+3 vs last week", "days in a row" — so a caller with a
         * sentence to say should put it under the grid rather than inside one
         * tile, where it silently pads every sibling.
         *
         * ⚠️ WHICH IS EXACTLY WHY THE DELTA JOINS IT INSTEAD OF STACKING UNDER
         * IT. `flex-wrap` means a narrow tile drops the caption to a second line
         * on its own terms rather than every tile in the row paying for a third
         * line up front.
         */
        <span
          className={`flex flex-wrap items-center gap-x-1.5 gap-y-0.5 font-dash text-muted ${
            dense ? "mt-1 text-[11px]" : "mt-1.5 text-dash-xs"
          }`}
        >
          {delta ? <StatDeltaChip delta={delta} /> : null}
          {caption ? <span className="min-w-0">{caption}</span> : null}
        </span>
      ) : null}
    </>
  );

  /*
   * 🔴 A REAL `<button>`, NOT A `<div onClick>`. The tile is one target with one
   * label, so the element that carries the handler has to be the one keyboards
   * and screen readers already know how to reach — Enter and Space for free, a
   * focus ring for free, and an announced role instead of a silent box. The
   * `text-left` is only undoing the centring a `<button>` brings with it.
   */
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={surface}
        className={`${shell} group cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface`}
      >
        {inner}
      </button>
    );
  }

  if (source) {
    return (
      <>
        <button
          type="button"
          aria-haspopup="dialog"
          title="Where this number comes from"
          onClick={() => {
            setSourceOpen(true);
          }}
          style={surface}
          className={`${shell} group cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface`}
        >
          {inner}
        </button>
        <SourceDialog
          open={sourceOpen}
          label={label}
          value={value}
          source={source}
          onClose={() => {
            setSourceOpen(false);
          }}
        />
      </>
    );
  }

  return (
    <div style={surface} className={shell}>
      {inner}
    </div>
  );
}

/**
 * The delta line — an arrow, a colour and the number, in that order.
 *
 * ⚠️ NOT A `StatusPill`. A pill is a bordered, tinted badge for a STATE ("Idle",
 * "Low accuracy") a teacher may need to act on; six of them across one stat row
 * would make the quietest line on the tile the loudest thing on the screen.
 * This is typography with an icon in front of it.
 */
function StatDeltaChip({ delta }: { delta: StatDelta }) {
  const { className, Icon } = DELTA_STYLE[delta.direction];
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 font-bold ${className}`}>
      {/* The arrow repeats the sign that is already in the text, so it is
          decorative to a screen reader rather than a second announcement. */}
      <span aria-hidden="true" className="inline-flex">
        <Icon size={12} strokeWidth={2.6} />
      </span>
      <span className="tabular">{delta.label}</span>
    </span>
  );
}

/**
 * The row of stat cards.
 *
 * 🔴 A GRID, NOT `flex-wrap`, AND THAT IS THE FIX FOR WHAT THE PLATFORM SCREEN
 * ACTUALLY LOOKED LIKE. Eight tiles laid out with `flex flex-wrap gap-3` and a
 * `min-w-40` produced five boxes of five different widths on the first line and
 * three ragged ones on the second, each sized by the length of its own label —
 * so "Children enrolled" was three times the width of "Teachers" and the eye
 * had no column to run down. A grid gives every metric the same box, which is
 * what makes a row of numbers scannable rather than merely present.
 *
 * ⚠️ THE COLUMN COUNT IS CAPPED AT FOUR. §1 designs for 1366×768; five columns
 * at that width leaves each card too narrow for a `dash-2xl` numeral beside a
 * two-word label.
 */
/**
 * A reward tile — the loud cousin of `StatCard`, for the three figures a child
 * earns rather than the ones an adult measures.
 *
 * 🔴 IT EXISTS BECAUSE THE SAME TILE HAD BEEN DRAWN TWICE, DIFFERENTLY. The
 * teacher's Leaderboard opened with four full-bleed gradient cards — coins,
 * stickers, streak, active children — and the student page showed the same
 * three figures for one child as plain `StatCard`s. A teacher moves between
 * those two screens constantly: same numbers, same words, two designs.
 *
 * 🔴 SO THE TONE REACHES THE WHOLE CARD HERE, WHERE `StatCard` KEEPS IT TO A
 * CORNER WASH — and the difference is deliberate rather than decorative.
 * `StatCard` carries measurements an adult compares across a row, so §4 holds
 * the numeral at `ink` on `surface` and spends no contrast on colour. These
 * three are a reward a teacher reads out to a five-year-old, and the colour is
 * the point: gold is coins, pink is stickers, green is the streak. The value
 * still clears contrast because it is mixed toward `ink`, never left as the
 * raw fill.
 *
 * ⚠️ `color-mix` AGAINST THE TOKENS, NOT PASTEL HEX LITERALS. The Leaderboard
 * version was `from-[#fffbeb] to-[#fef3c7]`, `text-amber-950`, `border-amber-200`
 * — six hardcoded colours per tile, times four tiles, none of which move when
 * the token set does. §1: colour comes from the tokens.
 *
 * ⚠️ AND THE WATERMARK IS THE ICON, NOT AN EMOJI. The originals put 🪙 ⭐ 🔥 👥
 * in both the disc and the watermark; `design-system.md` §6 reserves emoji for
 * game identity and §12 lists *"emoji as a UI icon"* on the Never list —
 * `scripts/check-emoji.mjs` is the gate for exactly this. An emoji here is also
 * a font-stack lottery on a school laptop, which is where this is read.
 */
export function RewardTile({
  label,
  value,
  caption,
  icon,
  tone = "sunshine",
}: {
  label: ReactNode;
  /** 🔴 A NUMBER OR A PRE-PAIRED FRACTION — never a percentage (OD-39/OD-72). */
  value: ReactNode;
  caption?: ReactNode;
  icon: ReactNode;
  tone?: StatTone;
}) {
  const fill = TONE_FILL[tone];

  return (
    <div
      className="play-card relative flex min-w-0 flex-col overflow-hidden rounded-lg border p-4 shadow-card sm:p-5"
      style={{
        /* ⚠️ A DIAGONAL WASH THAT NEVER REACHES FULL STRENGTH. 10 % → 22 % keeps
           the numeral legible on top of it; a solid fill would put a coloured
           ground under a figure §4 needs to stay readable at 36 px. */
        background: `linear-gradient(140% 140% at 0% 0%, color-mix(in srgb, ${fill} 10%, var(--color-surface)) 0%, color-mix(in srgb, ${fill} 22%, var(--color-surface)) 100%)`,
        borderColor: `color-mix(in srgb, ${fill} 34%, var(--color-line))`,
      }}
    >
      {/*
        ⚠️ THE WATERMARK IS `aria-hidden` AND SITS BEHIND THE TEXT. It is the
        same glyph as the disc, enlarged and faded — decoration that repeats the
        icon rather than adding a second thing to read. A screen reader hearing
        "coins" twice would be this tile telling the same fact two ways.
      */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-1 -right-1 opacity-25"
        style={{ color: fill }}
      >
        {icon}
      </span>

      <span className="relative flex items-center gap-2">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full shadow-xs"
          style={{
            background: "color-mix(in srgb, var(--color-surface) 80%, transparent)",
            color: fill,
          }}
        >
          {icon}
        </span>
        {/* 🔴 SENTENCE CASE, for `StatCard`'s reason — §12 lists all-caps
            micro-labels on the Never list. */}
        <span
          className="min-w-0 font-dash text-dash-xs font-bold"
          style={{ color: `color-mix(in srgb, ${fill} 45%, var(--color-ink))` }}
        >
          {label}
        </span>
      </span>

      {/* 🔴 TABULAR, AND MIXED TOWARD `ink` RATHER THAN LEFT AS THE FILL.
          `sunshine` is a fill and never a text colour (§2); at 78 % ink it is
          recognisably gold and still passes contrast on the wash behind it. */}
      <span
        className="tabular relative mt-2.5 text-dash-2xl leading-none font-extrabold"
        style={{ color: `color-mix(in srgb, ${fill} 22%, var(--color-ink))` }}
      >
        {value}
      </span>

      {caption ? (
        <span
          className="relative mt-1.5 font-dash text-dash-xs font-medium"
          style={{ color: `color-mix(in srgb, ${fill} 30%, var(--color-muted))` }}
        >
          {caption}
        </span>
      ) : null}
    </div>
  );
}

export function StatGrid({
  children,
  columns = 4,
}: {
  children: ReactNode;
  /**
   * How many tiles the widest breakpoint puts on a line. Four is the row every
   * tier has drawn; three is for a row of SIX.
   *
   * 🔴 IT EXISTS BECAUSE 6 ÷ 4 IS NOT A ROW, IT IS A ROW AND A STUB. The teacher
   * overview grew from four measures to six, and at `xl:grid-cols-4` that draws
   * four tiles and then two floating under the left half — the eye reads the
   * orphans as a different kind of thing, and the widths stop lining up with the
   * panels beneath. Three columns gives 3 + 3: two full lines, every tile the
   * same width, at every breakpoint from `sm` up.
   *
   * 🔴 AND SIX IS THE STRIP, WHICH IS THE SAME SIX TILES ON ONE LINE. Three
   * columns fixed the orphans and kept the boxes: 3 + 3 at `md` weight is 275px
   * of stat above a chart that starts below the fold on the 1366×768 laptop §1
   * designs for. Six columns of `size="xs"` tiles is ~105px for the same six
   * numbers, and the row reads left-to-right in one pass instead of two.
   *
   * ⚠️ SIX IS FOR `xs` TILES ONLY, AND THAT IS NOT A STYLE PREFERENCE. The
   * ceiling was four *because* five columns at 1366 leaves a card too narrow for
   * a `dash-2xl` numeral beside a two-word label — which is still true, and is
   * exactly what `xs` changes by dropping the numeral a notch and holding the
   * label to one line. Six columns of `md` tiles would be the original mistake
   * with a bigger number.
   *
   * 🔴 AND FIVE IS THE SAME STRIP ONE MEASURE SHORTER. The school overview
   * dropped its Consent tile and five tiles in a six-column grid is a row that
   * stops short of its own right edge — the mirror image of the orphan problem,
   * a hole where the eye expects a tile. Five columns of `xs` closes it and
   * keeps the single left-to-right pass; the `xs` argument above is what makes
   * a column this narrow legible, and it holds identically at five.
   *
   * ⚠️ THE CLASSES ARE STILL A CLOSED UNION rather than a `number`, so a caller
   * cannot ask for a column count Tailwind was never asked to generate.
   */
  columns?: 3 | 4 | 5 | 6;
}) {
  /*
   * ⚠️ THE GAP RAMPS WITH THE COLUMNS. Two columns and a 16px gutter inside the
   * shell's 16px inset leaves each tile 156px on a 360px phone, and a
   * `dash-2xl` numeral (36px) plus a 28px disc needs most of it. 12px gives each
   * tile 4px back, which is the difference between "1,248" fitting and wrapping.
   *
   * ⚠️ THE CLASSES ARE WRITTEN OUT, NOT BUILT. Tailwind scans source text, so
   * `xl:grid-cols-${columns}` compiles to a class that is never generated and a
   * grid that silently falls back to one column.
   *
   * ⚠️ AND THE SIX-COLUMN STRIP KEEPS THE PHONE'S 12px GUTTER ALL THE WAY UP.
   * Six tiles and five 16px gutters spend 80px of a 1068px content column on
   * air; at 12px each tile gets 3px back, and on a strip that narrow the tile is
   * where the pixels are worth more than the gap.
   */
  const columnClass =
    columns === 6
      ? "gap-3 sm:grid-cols-3 xl:grid-cols-6"
      : columns === 5
        ? "gap-3 sm:grid-cols-3 xl:grid-cols-5"
        : columns === 3
          ? "gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-3"
          : "gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-4";

  return (
    <div className={`grid grid-cols-2 ${columnClass}`}>
      {children}
    </div>
  );
}

/**
 * A labelled control — the wrapper every select and search box on an adult
 * screen sits in, so the label, the control and its hint form one block.
 */
export function Field({
  label,
  hint,
  htmlFor,
  children,
  width = "sm",
}: {
  label: ReactNode;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  width?: "sm" | "md" | "lg" | "full";
}) {
  /*
   * 🔴 THE CAP IS LIFTED BELOW `sm`, AND IT WAS THE CAP THAT BROKE THE PHONE.
   * `max-w-[15rem]` is a laptop measurement — it stops a two-word select
   * stretching across 1180px of toolbar. On a 360px screen the field's natural
   * width is already 328px, so the cap does nothing but leave 88px of dead space
   * beside a control a thumb has to hit. Three of them in a `Toolbar` wrapped
   * into a ragged staircase of different widths.
   *
   * ⚠️ `w-full` IS ON THE WRAPPER, NOT THE FIELD. `FIELD` is already `w-full`;
   * what it fills is this box, and this box was shrink-to-fit inside a wrapping
   * flex row. Without the `w-full` the `sm:max-w-*` has nothing to cap.
   */
  const max = {
    sm: "w-full sm:max-w-[15rem]",
    md: "w-full sm:max-w-sm",
    lg: "w-full sm:max-w-xl",
    full: "w-full",
  }[width];
  return (
    <div className={`flex flex-col gap-1.5 ${max}`}>
      <label
        className="font-dash text-dash-xs font-bold text-muted"
        htmlFor={htmlFor}
      >
        {label}
      </label>
      {children}
      {hint ? (
        <p className="font-dash text-dash-xs text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * The select.
 *
 * 🔴 THE NATIVE ARROW IS REPLACED, AND THAT IS NOT DECORATION. A bare
 * `<select>` renders the platform's own chrome — on Windows a grey-blue box
 * with a grey triangle — which is the one colour §1 forbids anywhere in this
 * product, sitting in the middle of a warm cream page. `appearance-none` plus
 * the design system's own chevron is the only way to keep the rule.
 *
 * ⚠️ THE OPTION LIST IS STILL THE OS's, and deliberately so. A custom listbox
 * is a keyboard-navigation and screen-reader surface to re-implement, and this
 * product's forms are five-option sorts, not comboboxes.
 */
export function Select({
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select
        {...rest}
        className={`${FIELD} cursor-pointer appearance-none pr-10 text-dash-sm`}
      >
        {children}
      </select>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2 rotate-90 text-muted"
      >
        <ChevronIcon size={16} />
      </span>
    </div>
  );
}

/** A text or search input, in the one field style. */
export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${FIELD} text-dash-sm`} />;
}

/**
 * A checkbox with its label and its consequence, as one pressable row.
 *
 * 🔴 IT EXISTS BECAUSE THE PARENT TIER HAD EIGHT BARE `<input type="checkbox">`
 * AND IT IS READ ON A PHONE. A default checkbox is 13 CSS pixels;
 * `information-architecture.md` §2 calls the parent tier *"a 30-second read on a
 * phone"*, and a 13px target beside an unclickable text label is the control a
 * parent misses twice before hitting it — on the screens that carry the WhatsApp
 * opt-in and the recording opt-out, where a mis-tap is a consent decision.
 *
 * ⚠️ THE WHOLE ROW IS THE TARGET, and it is a real `<label>` rather than a click
 * handler on a div — so the native checkbox keeps its keyboard behaviour, its
 * focus ring and its announcement to a screen reader. `accent-primary` tints the
 * OS control with the brand magenta instead of the platform's blue, which is the
 * same reason `Select` replaces its arrow.
 */
export function CheckboxRow({
  checked,
  onChange,
  children,
  hint,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  /** The consequence. On a consent control this is the important half. */
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-3 rounded-lg px-3 py-2.5 font-dash transition-colors duration-150 ease-dash ${
        disabled ? "opacity-50" : "cursor-pointer hover:bg-canvas"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
        className="mt-0.5 h-[1.125rem] w-[1.125rem] shrink-0 accent-primary"
      />
      <span className="min-w-0">
        <span className="block text-dash-sm font-bold text-ink">
          {children}
        </span>
        {hint ? (
          <span className="mt-0.5 block text-dash-xs text-muted">{hint}</span>
        ) : null}
      </span>
    </label>
  );
}

/**
 * A row of controls above a table — sorts, searches, and the count they act on.
 *
 * ⚠️ IT SITS ABOVE THE CARD RATHER THAN INSIDE IT. A control inside the table's
 * own border reads as belonging to the row under it; outside, it reads as
 * belonging to the list, which is what it does.
 */
export function Toolbar({ children }: { children: ReactNode }) {
  /*
   * ⚠️ A GRID BELOW `sm`, A WRAPPING ROW ABOVE IT. `flex-wrap` on a phone puts
   * each control on its own line at its own width, so a "Class" select, a
   * "Status" select and a search box stack as three different-length bars — the
   * ragged staircase `Field` above describes. One column at full width is the
   * shape a form has on a phone, and it costs nothing on a laptop because the
   * grid only exists below `sm`.
   */
  return (
    <div className="mb-3 grid grid-cols-1 items-end gap-3 sm:flex sm:flex-wrap sm:gap-4">
      {children}
    </div>
  );
}

/**
 * A sentence that matters, given the room to be read.
 *
 * 🔴 IT IS NOT A `StatusPill`, AND THE DIFFERENCE IS LENGTH. A pill is a label —
 * two or three words, inline, on a row. The platform and school tiers were
 * putting whole consequence sentences inside pills ("2 children are in no class
 * at all, so no school can see them…"), which produced a 700px-wide lozenge
 * with a rounded end floating in the middle of the page. Same tones, same
 * icon+colour+text rule (§12), a block instead of a chip.
 */
export type NoteTone = "success" | "warning" | "danger" | "neutral" | "info";

const NOTE_TONE: Record<NoteTone, { bg: string; fg: string; line: string }> = {
  success: {
    bg: "var(--color-pill-success-bg)",
    fg: "var(--color-pill-success-fg)",
    line: "var(--color-pill-success-line)",
  },
  warning: {
    bg: "var(--color-pill-warning-bg)",
    fg: "var(--color-pill-warning-fg)",
    line: "var(--color-pill-warning-line)",
  },
  danger: {
    bg: "var(--color-pill-danger-bg)",
    fg: "var(--color-pill-danger-fg)",
    line: "var(--color-pill-danger-line)",
  },
  neutral: {
    bg: "var(--color-pill-neutral-bg)",
    fg: "var(--color-pill-neutral-fg)",
    line: "var(--color-pill-neutral-line)",
  },
  /* The quietest of the five — a fact, not a verdict. Surface on canvas. */
  info: {
    bg: "var(--color-surface)",
    fg: "var(--color-ink-soft)",
    line: "var(--color-line)",
  },
};

export function Note({
  tone = "neutral",
  icon,
  children,
  /** A second line — the consequence, or what to do about it. */
  detail,
}: {
  tone?: NoteTone;
  /** 🔴 Required by §12 for every tone that carries meaning. */
  icon: ReactNode;
  children: ReactNode;
  detail?: ReactNode;
}) {
  const c = NOTE_TONE[tone];
  return (
    <div
      className="flex items-start gap-2.5 rounded-lg px-4 py-3 font-dash text-dash-sm"
      style={{ background: c.bg, color: c.fg, border: `1px solid ${c.line}` }}
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0">
        {icon}
      </span>
      <span className="max-w-[80ch]">
        <span className="font-bold">{children}</span>
        {detail ? (
          <span className="mt-1 block font-normal opacity-90">{detail}</span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * A sidebar entry.
 *
 * 🔴 A BUTTON, NOT A LINK, WHEN THE NAVIGATION IS A REDUCER. The teacher tier's
 * navigation is `teacherFlow`'s reducer rather than the hash — `routes.ts` has
 * exactly one teacher route — so those dispatch actions. The tiers whose nav IS
 * the hash pass `href`, and then this renders an anchor: a real URL in the
 * status bar, openable in a new tab, which a router-driven `<button>` is not.
 *
 * ⚠️ THE ACTIVE STATE IS A FILLED PILL, and it is the only place `primary` is
 * used as a large fill in this tier. §2 permits it: `primary #E5007A` is the
 * text-safe magenta and `primary-ink` is its paired foreground.
 */
export function NavItem({
  children,
  icon,
  active = false,
  tint: _tint,
  onClick,
  href,
  badge,
  badgeLabel,
  opensSheet,
}: {
  children: ReactNode;
  icon?: ReactNode;
  active?: boolean;
  /**
   * The item's identity colour — a token. See `nav.tsx`'s `NavPresentation`.
   *
   * 🔴 IT IS IGNORED WHEN THE ITEM IS ACTIVE, AND THAT IS THE POINT. The current
   * item is a filled magenta pill; a second colour inside it would put two
   * meanings on one row and drop the icon's contrast against the fill. Identity
   * is what you use to *find* a row, so it belongs on the resting state.
   */
  tint?: string;
  onClick: () => void;
  /** Present when the item is a route. Absent when it dispatches an action. */
  href?: string;
  /**
   * How many things are waiting behind this item. `0` draws nothing.
   *
   * 🔴 A COUNT, NOT A BOOLEAN DOT. A dot says "something happened"; a reader
   * then has to open the screen to find out whether it is one message or nine,
   * which is the click the badge exists to save. OD-39/OD-72's rule — no count
   * without meaning — cuts the other way here: the number IS the meaning.
   *
   * ⚠️ IT IS ANNOUNCED, NOT PAINTED. §12 forbids colour as the only carrier, and
   * a magenta disc beside a word is exactly that for a screen-reader user — so
   * the badge carries its own `aria-label` from `badgeLabel` and the disc itself
   * is `aria-hidden`.
   */
  badge?: number;
  /** What the badge means, for the reader who cannot see it. */
  badgeLabel?: (n: number) => string;
  /**
   * Set when the item opens a sheet rather than a page — the thumb bar's
   * "More". Its value is whether that sheet is open.
   *
   * ⚠️ `active` THEN MEANS "THE PAGE YOU ARE ON IS IN HERE", so it is announced
   * as `aria-current="true"` rather than `"page"`: the item is not the page.
   */
  opensSheet?: boolean;
}) {
  /*
   * ⚠️ `transition-colors` BECAME `transition` — the row now moves 2px on hover,
   * and a colour-only transition would leave the slide unsmoothed. Both are
   * removed wholesale under either reduce-motion switch by `play.css`'s
   * `.play-nav`, which is why the class is here rather than a `hover:translate`
   * inline: the rule has one home.
   */
  /*
   * The console nav item (Figma: Components › Nav Item). Monochrome: the icon
   * takes the label's colour, and "active" is a white chip lifted off the muted
   * sidebar by a hairline and a 1px shadow — never a fill colour. `tint` is
   * still accepted so callers need not change, and is deliberately unused.
   */
  const className = `play-nav flex w-full h-[30px] items-center gap-2.5 rounded-[7px] border pr-2 pl-2.5 text-left font-dash text-[13px] leading-5 transition-colors duration-150 ${
    active
      ? "border-line bg-surface font-medium text-ink shadow-[0_1px_1px_rgba(0,0,0,0.06)]"
      : "border-transparent font-normal text-muted hover:bg-[rgba(20,20,19,0.04)] hover:text-ink"
  }`;

  const inner = (
    <>
      {icon ? (
        <span
          aria-hidden="true"
          className="play-nav-disc inline-flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4"
        >
          {icon}
        </span>
      ) : (
        /*
         * 🔴 AN EMPTY DISC-SIZED BOX, NOT NOTHING. "Sign out" is the one nav item
         * in the product with no icon — §6 ships twenty glyphs and none of them
         * means "leave", and inventing one is exactly what that section forbids.
         * Without this the label started 44px to the left of every other row in
         * the column, so the last item in the sidebar was the one that looked
         * misaligned. Reserving the space costs nothing and keeps one left edge
         * down the whole list.
         *
         * ⚠️ `aria-hidden` AND EMPTY: it is a spacer, and a screen reader should
         * not hear a thing about it.
         */
        <span aria-hidden="true" className="inline-flex h-4 w-4 shrink-0" />
      )}
      {/*
       * 🔴 THE LABEL IS WRAPPED, AND THE WRAPPER IS THE WHOLE POINT. It used to
       * be a bare text node, and `play.css` truncates the thumb bar's label with
       * `.play-nav > :last-child` — a selector that can only ever match an
       * ELEMENT. So it matched the disc (or, on Messages, the badge) and applied
       * `text-overflow: ellipsis` to a 28px circle, while the words themselves —
       * unwrappable, unclippable — ran straight out of the item's box and over
       * the item beside it. On a 360px phone all six labels overlapped their
       * neighbours and the active pill sat on top of the next word along; the
       * tier's ONLY navigation was six unreadable, overlapping targets.
       *
       * ⚠️ `min-w-0` IS LOAD-BEARING ON A FLEX CHILD. Its default `min-width:
       * auto` refuses to shrink below the text's own width, so the ellipsis
       * would never fire and the wrapper would push the item wide instead — the
       * same overlap, one element further in.
       */}
      <span className="play-nav-label min-w-0">{children}</span>
      {badge !== undefined && badge > 0 && (
        /*
          ⚠️ `ml-auto` PUSHES IT TO THE TRAILING EDGE rather than sitting against
          the label, so the badges line up down the column whatever the words are
          — which is what lets a reader find "the one with something on it"
          without reading any of them.
        */
        <>
          <span
            aria-hidden="true"
            className="tabular ml-auto shrink-0 rounded-[6px] border border-line bg-surface px-1.5 text-[11.5px] leading-4 font-medium text-ink-soft"
          >
            {badge > 99 ? "99+" : badge}
          </span>
          <span className="sr-only">
            {badgeLabel ? badgeLabel(badge) : String(badge)}
          </span>
        </>
      )}
    </>
  );

  if (href !== undefined) {
    return (
      <a
        href={href}
        aria-current={active ? "page" : undefined}
        onClick={(e) => {
          e.preventDefault();
          onClick();
        }}
        className={className}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? (opensSheet === undefined ? "page" : "true") : undefined}
      aria-haspopup={opensSheet === undefined ? undefined : "dialog"}
      aria-expanded={opensSheet}
      className={className}
    >
      {inner}
    </button>
  );
}

/** A quiet grouping label inside the sidebar — "Class 2", "Class 1". */
export function NavGroup({ children }: { children: ReactNode }) {
  return (
    /* ⚠️ 16px, not 20 — 20 is off the spacing scale, and this is a label inside
       a sidebar list whose own items are 10px apart. */
    <p className="px-2.5 pt-3.5 pb-1 font-dash text-[11.5px] leading-4 font-medium text-muted">
      {children}
    </p>
  );
}
